import { Link } from "react-router-dom";

/**
 * A "track row" for documents — square cover on the left, text on the right,
 * the way a row reads in a music app. Replaces the old full-width banner cards.
 *
 * Fully responsive: the cover is a fixed square that scales by breakpoint, the
 * text column is `min-w-0` + line-clamped so long titles/subtitles never overflow
 * or blow out the row height at any width.
 */
export function DocumentTrack({
  to,
  coverImage,
  eyebrow,
  title,
  subtitle,
  caption,
}: {
  to: string;
  coverImage?: string | null;
  eyebrow?: string | null;
  title: string;
  subtitle?: string | null;
  caption?: string | null;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 sm:gap-5 py-3 border-b border-[var(--color-rule)] hover:bg-[var(--color-vellum-dim)] transition-colors"
    >
      <div className="media-frame flex-none w-16 h-16 sm:w-20 sm:h-20">
        {coverImage ? (
          <img
            src={coverImage}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full grid place-items-center font-display text-2xl text-[var(--color-ink-light)]">
            ॐ
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {eyebrow && (
          <p className="font-grotesk text-[0.6rem] sm:text-[0.62rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] mb-0.5">
            {eyebrow}
          </p>
        )}
        <p className="font-display text-[1.05rem] sm:text-[1.2rem] leading-[1.18] tracking-tight line-clamp-2 group-hover:text-[var(--color-violet)] transition-colors">
          {title}
        </p>
        {subtitle && (
          <p className="font-display-italic text-[0.9rem] sm:text-[0.95rem] text-[var(--color-ink-mute)] leading-[1.3] line-clamp-1 mt-0.5">
            {subtitle}
          </p>
        )}
        {caption && (
          <p className="font-grotesk text-[0.62rem] tracking-[0.14em] uppercase text-[var(--color-ink-mute)] mt-1 line-clamp-1">
            {caption}
          </p>
        )}
      </div>

      <span
        aria-hidden
        className="flex-none text-[var(--color-ink-light)] group-hover:text-[var(--color-violet)] transition-colors pr-1"
      >
        →
      </span>
    </Link>
  );
}
